import { Injectable, inject, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, timer, Subject, merge, EMPTY, Subscription } from 'rxjs';
import { map, shareReplay, switchMap, takeUntil, catchError, filter } from 'rxjs/operators';
import { NotificationCenter, NotificationType } from '../interfaces/notification';
import { NotificationCenterApiService } from './notification-center-api.service';
import { LoggerService } from './logger.service';
import { ConfirmationService } from './confirmation.service';
import { TranslateService } from '@ngx-translate/core';
import { EventService } from './event.service';
import { Router } from '@angular/router';
import { HotelService } from './hotel.service';
import { UserService } from './user.service';

export interface NotificationFilters {
  type?: NotificationType;
  isRead?: boolean;
}

export interface NotificationState {
  notifications: NotificationCenter[];
  total: number;
  unreadCount: number;
  page: number;
  perPage: number;
  lastPage: number;
  loading: boolean;
  error: string | null;
  filters: NotificationFilters;
  tab: 'active' | 'archived';
}

@Injectable({
  providedIn: 'root'
})
export class NotificationCenterService implements OnDestroy {
  private readonly api = inject(NotificationCenterApiService);
  private readonly logger = inject(LoggerService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);
  private readonly eventService = inject(EventService);
  private readonly router = inject(Router);
  private readonly hotelService = inject(HotelService);
  private readonly userService = inject(UserService);
  private readonly destroy$ = new Subject<void>();

  // Subscription to the 'userChanged' event, cleaned up in ngOnDestroy.
  private loginRefreshSubscription: Subscription | null = null;

  private readonly _state$ = new BehaviorSubject<NotificationState>({
    notifications: [],
    total: 0,
    unreadCount: 0,
    page: 1,
    perPage: 10,
    lastPage: 1,
    loading: false,
    error: null,
    filters: {},
    tab: 'active'
  });

  public readonly state$ = this._state$.asObservable();
  public readonly notifications$ = this.state$.pipe(map(state => state.notifications));
  public readonly unreadCount$ = this.state$.pipe(map(state => state.unreadCount));
  public readonly loading$ = this.state$.pipe(map(state => state.loading));
  public readonly error$ = this.state$.pipe(map(state => state.error));

  // Polls unread count immediately, again at 5 s (catches notifications the backend
  // creates asynchronously right after login), then every 30 s thereafter.
  // catchError on the inner observable is critical: without it, a single 401
  // (e.g. the poll that runs just after logout) terminates the entire stream and
  // the badge never updates again until the user refreshes the page.
  private readonly _realTimeUpdates$ = merge(
    timer(0, 30000),
    timer(5000)
  ).pipe(
    // No token means the session is over (logout, or the error interceptor cleared
    // it after a 401). Polling on would only produce one more 401 every tick for as
    // long as the tab stays open. Polling picks up again once a new login stores a token.
    filter(() => !!this.userService.getToken()),
    switchMap(() => this.api.getUnreadCount().pipe(
      catchError(() => EMPTY)
    )),
    takeUntil(this.destroy$),
    shareReplay(1)
  );

  constructor() {
    this._realTimeUpdates$.subscribe({
      next: (count: number) => {
        const previousCount = this._state$.value.unreadCount;
        this.updateState({ unreadCount: count });

        // When new notifications arrive (count increased), also refresh the list.
        // Without this, the badge updates every 30 s but the dropdown still shows
        // the stale list — including the case where a notification is created by
        // the backend asynchronously just after login (e.g. "Login from new device").
        if (count > previousCount) {
          this.loadNotifications(true);
        }
      },
      error: (error) => {
        this.logger.error('Failed to poll unread count:', error);
      }
    });

    this.loadNotifications();

    // When the user logs back in after a logout (without a full page refresh),
    // _realTimeUpdates$ may be up to 30 s away from its next tick.
    // Re-poll immediately on login so the badge is current right away.
    // 'userChanged' is broadcast by UserService.resolveUser() after successful auth.
    this.loginRefreshSubscription = this.eventService.subscribe('userChanged', (payload) => {
      if (!payload) return;
      this.api.getUnreadCount().pipe(
        catchError(() => EMPTY),
        takeUntil(this.destroy$)
      ).subscribe(count => {
        this.updateState({ unreadCount: count });
        this.loadNotifications(true);
      });
    });

    // Reload when the UI language changes so server-translated
    // title/body/time_ago come back in the current locale.
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadNotifications(true));
  }

  loadNotifications(reset = false): void {
    const currentState = this._state$.value;
    const page = reset ? 1 : currentState.page;

    this.updateState({ loading: true, error: null });

    this.api.getNotifications(currentState.tab, page, currentState.perPage, currentState.filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const notifications = reset
            ? response.notifications
            : [...currentState.notifications, ...response.notifications];

          this.updateState({
            notifications,
            total: response.total,
            page: response.page,
            lastPage: response.lastPage,
            loading: false
          });
        },
        error: (error) => {
          this.updateState({
            loading: false,
            error: this.translate.instant('notifications.notification_center.load_failed')
          });
          this.logger.error('Failed to load notifications:', error);
        }
      });
  }

  loadMore(): void {
    const currentState = this._state$.value;
    if (currentState.notifications.length < currentState.total && !currentState.loading) {
      this.updateState({ page: currentState.page + 1 });
      this.loadNotifications();
    }
  }

  switchTab(tab: 'active' | 'archived'): void {
    this.updateState({ tab, page: 1 });
    this.loadNotifications(true);
  }

  /**
   * Resets the notification state to defaults (active tab, page 1, no filters)
   * and reloads. Called on page init to avoid stale singleton state from a
   * previous visit (e.g. user left on the archived tab, came back expecting active).
   */
  resetToDefaults(): void {
    this.updateState({ tab: 'active', page: 1, filters: {} });
    this.loadNotifications(true);
  }

  /**
   * Reloads notifications from page 1 while keeping the current tab and filters.
   * Used on locale change so the active tab is preserved and the API receives
   * the updated locale with the correct tab parameter.
   */
  reloadForLocaleChange(): void {
    this.updateState({ page: 1 });
    this.loadNotifications(true);
  }

  applyFilters(filters: NotificationFilters): void {
    this.updateState({ filters: { ...filters }, page: 1 });
    this.loadNotifications(true);
  }

  markAsRead(notificationUuid: string): void {
    this.api.markAsRead(notificationUuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedNotification: NotificationCenter) => {
          this.updateNotificationInState(updatedNotification);
          this.refreshUnreadCount();
        },
        error: (error) => {
          this.logger.error('Failed to mark notification as read:', error);
          this.updateState({ error: this.translate.instant('notifications.notification_center.mark_read_failed') });
        }
      });
  }

  markAllAsRead(): void {
    this.api.markAllAsRead()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const currentState = this._state$.value;
          const updatedNotifications = currentState.notifications.map(n => ({
            ...n,
            isRead: true
          }));
          this.updateState({
            notifications: updatedNotifications,
            unreadCount: 0
          });
        },
        error: (error) => {
          this.logger.error('Failed to mark all as read:', error);
          this.updateState({ error: this.translate.instant('notifications.notification_center.mark_all_read_failed') });
        }
      });
  }

  archiveNotification(notificationUuid: string): void {
    this.eventService.broadcast('globalLoader:show', null);
    this.api.archiveNotification(notificationUuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.eventService.broadcast('globalLoader:hide', null);
          this.removeNotificationFromState(notificationUuid);
          this.refreshUnreadCount();
          this.confirmationService.swalSuccess(
            this.translate.instant('notifications.notification_center.archived')
          );
        },
        error: (error) => {
          this.eventService.broadcast('globalLoader:hide', null);
          this.logger.error('Failed to archive notification:', error);
          this.updateState({ error: this.translate.instant('notifications.notification_center.archive_failed') });
        }
      });
  }

  deleteNotification(notificationUuid: string): void {
    this.eventService.broadcast('globalLoader:show', null);
    this.api.deleteNotification(notificationUuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.eventService.broadcast('globalLoader:hide', null);
          this.removeNotificationFromState(notificationUuid);
          this.refreshUnreadCount();
          this.confirmationService.swalSuccess(
            this.translate.instant('notifications.notification_center.deleted')
          );
        },
        error: (error) => {
          this.eventService.broadcast('globalLoader:hide', null);
          this.logger.error('Failed to delete notification:', error);
          this.updateState({ error: this.translate.instant('notifications.notification_center.delete_failed') });
        }
      });
  }

  trackCtaClick(notificationUuid: string): void {
    this.api.trackCtaClick(notificationUuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (error) => {
          this.logger.error('Failed to track CTA click:', error);
        }
      });
  }

  getNotificationsByType(type: NotificationType): Observable<NotificationCenter[]> {
    return this.notifications$.pipe(
      map(notifications => notifications.filter(n => n.type === type))
    );
  }

  /**
   * Navigates to the target page for a notification.
   * Switches the active hotel context when the notification belongs to a hotel,
   * then resolves the correct URL based on the notification type.
   * Called by the dashboard, the header bell dropdown, and the notifications page.
   */
  navigateToNotification(notification: NotificationCenter): void {
    if (!notification.actionUrl) {
      return;
    }

    this.trackCtaClick(notification.uuid);

    const hotelId = notification.hotel?.id?.toString() ?? this.hotelService.hotelId ?? undefined;
    const targetUrl = this.resolveNotificationUrl(notification, hotelId);

    if (hotelId) {
      this.hotelService.initializeSelectedHotel(hotelId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => this.router.navigateByUrl(targetUrl),
          error: () => this.router.navigateByUrl(targetUrl)
        });
      return;
    }

    this.router.navigateByUrl(targetUrl);
  }

  private resolveNotificationUrl(notification: NotificationCenter, hotelId: string | undefined): string {
    switch (notification.type) {
      case NotificationType.BOOKING:
        return notification.actionUrl!;

      case NotificationType.HOTEL_INFO:
        return `/${hotelId}${notification.actionUrl}`;

      case NotificationType.HOTEL_STATUS:
        return `/${hotelId}/dashboard`;

      case NotificationType.HOTEL_SETTINGS:
        return `/${hotelId}/hotel-settings`;

      default:
        return notification.actionUrl!.startsWith('/')
          ? notification.actionUrl!
          : `/${notification.actionUrl!}`;
    }
  }

  ngOnDestroy(): void {
    this.loginRefreshSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  clearError(): void {
    this.updateState({ error: null });
  }

  private updateState(update: Partial<NotificationState>): void {
    this._state$.next({ ...this._state$.value, ...update });
  }

  private updateNotificationInState(updatedNotification: NotificationCenter): void {
    const currentState = this._state$.value;
    const notifications = currentState.notifications.map(n =>
      n.uuid === updatedNotification.uuid ? updatedNotification : n
    );
    this.updateState({ notifications });
  }

  private removeNotificationFromState(notificationUuid: string): void {
    const currentState = this._state$.value;
    const notifications = currentState.notifications.filter(n => n.uuid !== notificationUuid);
    this.updateState({
      notifications,
      total: currentState.total - 1
    });
  }

  private refreshUnreadCount(): void {
    this.api.getUnreadCount()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (count: number) => {
          this.updateState({ unreadCount: count });
        },
        error: (error) => {
          this.logger.error('Failed to refresh unread count:', error);
        }
      });
  }
}
